import React, { useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { MapPin, Heart, Clock, Briefcase, Award, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import OptimizedImage from '@/components/OptimizedImage';
import SmartLogo from '@/components/SmartLogo';
import ListingTypeBadge from '@/components/ListingTypeBadge';
import { getListingPriceDisplay } from '@/utils/listingPriceConfig';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useFavorites } from '@/hooks/useFavorites';
import heroPlaceholder from '@/assets/hero-helicopter-new.jpg';
import chopperSwapLogo from '@/assets/chopperswap-logo-no-bg.png';
import { Button } from '@/components/ui/button';
import { ShareListingButton } from '@/components/ShareListingButton';
import { Badge } from '@/components/ui/badge';
import { getServiceTypeMapPin, getServiceCategoryShortLabel } from '@/utils/serviceTypeIcons';
import { getJobTypeMapPin, isJobOffering } from '@/utils/jobTypeIcons';
import { formatJobSalary } from '@/utils/formatJobSalary';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { useAuth } from '@/components/AuthProvider';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';

const BrandedListingFallback: React.FC = () => (
  <div className="flex h-full w-full items-center justify-center bg-card dark:bg-background">
    <img
      src={chopperSwapLogo}
      alt="ChopperSwap"
      className="h-auto max-h-[58%] w-2/3 max-w-[72%] object-contain opacity-90 dark:brightness-0 dark:invert dark:opacity-95"
    />
  </div>
);

interface MobileListViewProps {
  listing: {
    id: string;
    title: string;
    location?: string;
    price?: number;
    listingType?: string;
    heroImage?: string;
    year?: number;
    flightHours?: number;
    // Asset type awareness
    assetType?: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
    // Rental-specific fields
    rentalRate?: number;
    rentalPeriod?: string;
    maxBudget?: number;
    // Job-specific fields
    jobCategory?: string;
    employmentType?: string;
    salaryMin?: number;
    salaryMax?: number;
    salaryType?: string;
    companyName?: string;
    companyLogo?: string;
    typeRatings?: string[];
    // Service-specific fields
    serviceCategory?: string;
    shiftCoverage?: string;
    aogSupport?: boolean;
    responseTime?: string;
    turnaroundTime?: string;
    pricingModel?: string;
    baseRate?: number;
    rateUnit?: string;
    // Parts-specific
    partsData?: {
      itemType?: string;
      specificityType?: string;
      rentalRate?: number;
      rentalPeriod?: string;
    };
    isBuyRequest?: boolean;
    sellerId?: string;
    visibilitySettings?: any;
  };
  isSelected?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}


// Format shift coverage for display
const formatShiftCoverage = (coverage?: string): string => {
  if (!coverage) return '';
  const coverageMap: Record<string, string> = {
    '24_7': '24/7',
    'two_shift': '2 Shifts',
    'day_shift': 'Day Shift',
    'business_hours': 'Business Hours',
    'on_call': 'On Call'
  };
  return coverageMap[coverage] || coverage;
};

// Format job category for display
const formatJobCategory = (category?: string): string => {
  if (!category) return '';
  const categoryMap: Record<string, string> = {
    'pilot': 'Pilot',
    'mechanic': 'Mechanic',
    'camo': 'CAMO',
    'part_21': 'Part-21',
    'cabin_crew': 'Cabin Crew',
    'ground_ops': 'Ground Ops',
    'atc_dispatch': 'ATC/Dispatch',
    'training': 'Training',
    'drones_uas': 'UAS',
    'airport_ops': 'Airport Ops',
    'engineering': 'Engineering',
    'management': 'Management'
  };
  return categoryMap[category] || category.replace(/_/g, ' ');
};

// Format employment type for display
const formatEmploymentType = (type?: string): string => {
  if (!type) return '';
  const typeMap: Record<string, string> = {
    'full_time': 'Full-Time',
    'part_time': 'Part-Time',
    'contract': 'Contract',
    'freelance': 'Freelance',
    'internship': 'Internship',
    'seasonal': 'Seasonal'
  };
  return typeMap[type] || type.replace(/_/g, ' ');
};

export const MobileListView: React.FC<MobileListViewProps> = ({
  listing,
  isSelected = false,
  onClick,
  style
}) => {
  const { currency } = useCurrency();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const { openUnifiedContact } = useUnifiedContact();

  // Privacy gating
  const { blurPrice: shouldHidePrice, blurLocation: shouldHideLocation, blurContact: shouldHideContact } =
    useEffectiveVisibility(listing.id, listing.visibilitySettings, listing.sellerId);
  const isOwner = user?.id === listing.sellerId;

  const requestField = useCallback((field: string) => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    if (!listing.sellerId || listing.sellerId === user.id) return;
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.sellerId,
      assetType: listing.assetType || 'helicopter',
      hiddenFields: { price: shouldHidePrice, location: shouldHideLocation, contact: shouldHideContact },
      preselectedFields: [field],
    });
  }, [user, listing.id, listing.title, listing.sellerId, listing.assetType, shouldHidePrice, shouldHideLocation, shouldHideContact, openUnifiedContact]);
  
  // Determine listing display modes
  const isCompanyListing = 
    (listing.assetType === 'services' && (listing.listingType === 'service_offer' || listing.listingType === 'capability_listing')) ||
    (listing.assetType === 'jobs' && (listing.listingType === 'job_post' || listing.listingType === 'job_contract'));
  
  const isProfileListing = 
    listing.assetType === 'jobs' && (listing.listingType === 'job_profile' || listing.listingType === 'job_seeking');
  
  // Smart image selection based on listing type
  const primaryImage = isCompanyListing && listing.companyLogo 
    ? listing.companyLogo 
    : isProfileListing
      ? (listing.heroImage || listing.companyLogo || undefined)
      : (listing.heroImage || undefined);
  
  const isFav = isFavorite(listing.id);

  // Use centralized price display logic for consistent formatting
  const priceResult = getListingPriceDisplay({
    assetType: listing.assetType || 'helicopter',
    listingType: listing.listingType || 'sale',
    price: listing.price,
    isBuyRequest: listing.isBuyRequest,
    maxBudget: listing.maxBudget || listing.price,
    rentalRate: listing.rentalRate || listing.partsData?.rentalRate,
    rentalPeriod: listing.rentalPeriod || listing.partsData?.rentalPeriod,
    // Service-specific
    pricingModel: listing.pricingModel,
    baseRate: listing.baseRate,
    rateUnit: listing.rateUnit,
    // Job-specific
    salaryMin: listing.salaryMin,
    salaryMax: listing.salaryMax,
    salaryType: listing.salaryType,
  }, currency);

  // Map assetType to favoriteType - includes services
  const getFavoriteType = (): 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services' => {
    const assetType = listing.assetType || 'helicopter';
    if (assetType === 'services') return 'services';
    if (assetType === 'jobs') return 'jobs';
    if (assetType === 'parts') return 'parts';
    if (assetType === 'engine') return 'engine';
    return 'helicopter';
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(listing.id, getFavoriteType());
  };

  // Determine if we should show price or alternative info
  const hasJobSalary = listing.assetType === 'jobs' && (listing.salaryMin || listing.salaryMax);

  return (
    <Card 
      className={cn(
        "relative flex gap-3 p-3 min-h-[120px] cursor-pointer touch-pan-y",
        "transition-all duration-300 ease-out",
        "bg-card/80 backdrop-blur-sm",
        "border border-border/50",
        "hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5",
        "active:scale-[0.98] active:opacity-90",
        isSelected && [
          "z-20",
          "ring-2 ring-[hsl(45,93%,47%)] ring-offset-4 ring-offset-background",
          "bg-amber-50/30 dark:bg-amber-950/20",
          "border-[hsl(45,93%,47%)]",
          "shadow-xl shadow-[hsl(45,93%,47%)]/30"
        ]
      )}
      onClick={onClick}
      style={style}
    >
      {/* Image with enhanced styling */}
      <div className={cn(
        "relative w-[110px] h-[110px] flex-shrink-0 rounded-xl overflow-hidden",
        "bg-muted/50",
        "shadow-inner"
      )}>
        {isProfileListing && primaryImage ? (
          // Professional profile - circular photo with gradient background (like grid view)
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-800 dark:to-slate-900">
            <div className="w-[70px] h-[70px] rounded-full overflow-hidden ring-2 ring-white dark:ring-slate-700 shadow-lg">
              <img
                src={primaryImage}
                alt={listing.title}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = heroPlaceholder; }}
              />
            </div>
          </div>
        ) : isCompanyListing && primaryImage ? (
          <SmartLogo
            src={primaryImage}
            alt={listing.title}
            variant="card"
          />
        ) : !primaryImage ? (
          <BrandedListingFallback />
        ) : (
          <OptimizedImage
            src={primaryImage}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}
        
        {/* Gradient overlay - only for non-profile images */}
        {!isProfileListing && !isCompanyListing && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        )}
        
        {/* Services: Only category badge on image (no type pin) */}
        {listing.assetType === 'services' && listing.serviceCategory && (
          <div className="absolute top-2 left-2 z-10">
            <Badge className="text-[9px] px-1.5 py-0.5 shadow-md font-bold bg-primary/90 text-primary-foreground">
              {getServiceCategoryShortLabel(listing.serviceCategory)}
            </Badge>
          </div>
        )}

        {/* Jobs: Only category badge on image (no type pin) */}
        {listing.assetType === 'jobs' && listing.jobCategory && (
          <div className="absolute top-2 left-2 z-10">
            <Badge className="text-[9px] px-1.5 py-0.5 shadow-md font-bold bg-slate-700 text-white">
              {formatJobCategory(listing.jobCategory)}
            </Badge>
          </div>
        )}
        
        {/* Listing type badge - ONLY for helicopter/engine/parts */}
        {listing.assetType !== 'jobs' && listing.assetType !== 'services' && (
          <div className="absolute top-2 left-2 z-10">
            <ListingTypeBadge 
              listingType={listing.listingType || 'sale'} 
              itemType={listing.partsData?.itemType as 'equipment' | 'part' | 'tool' | undefined}
              specificityType={listing.partsData?.specificityType as 'engine' | 'general' | 'helicopter' | undefined}
              className="text-[10px] px-1.5 py-0.5 shadow-sm"
            />
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        {/* Top: Title with type indicator for jobs/services */}
        <div className="space-y-1.5">
          <div className="flex items-start gap-2">
            {/* Type pin for jobs/services - inline with title */}
            {(listing.assetType === 'jobs' || listing.assetType === 'services') && (
              <div className={cn(
                "w-6 h-6 rounded-full bg-white dark:bg-slate-800 shadow-md flex items-center justify-center flex-shrink-0 mt-0.5",
                listing.assetType === 'jobs' 
                  ? (isJobOffering(listing.listingType) ? "ring-1 ring-emerald-400" : "ring-1 ring-blue-400")
                  : ((listing.listingType === 'service_offer' || listing.listingType === 'capability_listing') ? "ring-1 ring-emerald-400" : "ring-1 ring-amber-400")
              )}>
                <img 
                  src={listing.assetType === 'jobs' ? getJobTypeMapPin(listing.listingType) : getServiceTypeMapPin(listing.listingType)} 
                  alt="" 
                  className="w-4 h-4"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            )}
            <h4 className="font-semibold text-sm line-clamp-2 leading-tight text-foreground">
              {listing.title}
            </h4>
          </div>
          {shouldHideLocation ? (
            <BlurredPrivateData
              placeholderContent={
                <div className="flex items-center gap-1 text-xs text-muted-foreground max-w-full">
                  <MapPin className="w-3 h-3 flex-shrink-0 text-primary/60" />
                  <span className="truncate max-w-[calc(100%-1rem)]">{listing.location || 'Location hidden'}</span>
                </div>
              }
              fieldName="Location"
              size="sm"
              onRequestInfo={isOwner ? undefined : () => requestField('location')}
            />
          ) : (
            <div className="flex items-center gap-1 text-xs text-muted-foreground max-w-full">
              <MapPin className="w-3 h-3 flex-shrink-0 text-primary/60" />
              <span className="truncate max-w-[calc(100%-1rem)]">{listing.location || 'Location not specified'}</span>
            </div>
          )}
          
          {/* Asset-specific metadata row */}
          {listing.assetType === 'jobs' && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground/80">
              {listing.employmentType && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatEmploymentType(listing.employmentType)}
                </span>
              )}
              {listing.typeRatings && listing.typeRatings.length > 0 && (
                <span className="flex items-center gap-1 text-[10px] bg-muted/50 px-1.5 py-0.5 rounded">
                  <Award className="w-2.5 h-2.5" />
                  {listing.typeRatings.length} ratings
                </span>
              )}
            </div>
          )}
          
          {listing.assetType === 'services' && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground/80">
              {listing.shiftCoverage && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatShiftCoverage(listing.shiftCoverage)}
                </span>
              )}
              {listing.aogSupport && (
                <Badge variant="destructive" className="text-[8px] px-1 py-0 h-4">
                  AOG
                </Badge>
              )}
            </div>
          )}
          
          {/* Traditional year/hours for helicopters/engines */}
          {(listing.assetType === 'helicopter' || listing.assetType === 'engine') && listing.year && (
            <div className="text-xs text-muted-foreground/80">
              {listing.year}
              {listing.flightHours && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-muted/50 rounded text-[10px]">
                  {listing.flightHours.toLocaleString()} hrs
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Bottom: Price/Info + Actions */}
        <div className="flex items-center justify-between gap-2 mt-2">
          {/* Smart info display based on asset type */}
          {listing.assetType === 'jobs' ? (
            // Jobs: Show salary or category
            <div className="flex flex-col gap-0.5">
              {shouldHidePrice ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className="px-2 py-1 rounded-md shadow-sm bg-muted/30 border border-border/40">
                      <span className="text-[8px] text-muted-foreground block uppercase tracking-wide font-medium">Salary</span>
                      <div className="text-sm font-bold text-foreground">$XX,XXX</div>
                    </div>
                  }
                  fieldName="Salary"
                  size="sm"
                  onRequestInfo={isOwner ? undefined : () => requestField('price')}
                />
              ) : hasJobSalary ? (
                <div className={cn(
                  "px-2 py-1 rounded-md shadow-sm",
                  isJobOffering(listing.listingType)
                    ? "bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-950/10 border border-emerald-200/60 dark:border-emerald-800/40"
                    : "bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-950/10 border border-amber-200/60 dark:border-amber-800/40"
                )}>
                  <span className="text-[8px] text-muted-foreground block uppercase tracking-wide font-medium">
                    {isJobOffering(listing.listingType) ? 'Salary' : 'Expected'}
                  </span>
                  <div className="text-sm font-bold text-foreground">
                    {formatJobSalary(listing.salaryMin, listing.salaryMax, listing.salaryType, 'Competitive', currency)}
                  </div>
                </div>
              ) : (
                <div className="px-2 py-1.5 rounded-md bg-muted/30 border border-border/40">
                  <span className="text-xs font-medium text-muted-foreground">View Details</span>
                </div>
              )}
            </div>
          ) : listing.assetType === 'services' ? (
            // Services: Show response time or category info
            <div className="flex flex-col gap-0.5">
              {listing.responseTime || listing.turnaroundTime ? (
                <div className={cn(
                  "px-2 py-1 rounded-md shadow-sm",
                  (listing.listingType === 'service_offer' || listing.listingType === 'capability_listing')
                    ? "bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-950/10 border border-emerald-200/60 dark:border-emerald-800/40"
                    : "bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-950/10 border border-amber-200/60 dark:border-amber-800/40"
                )}>
                  <span className="text-[8px] text-muted-foreground block uppercase tracking-wide font-medium">
                    Response
                  </span>
                  <div className="text-sm font-bold text-foreground">
                    {listing.responseTime || listing.turnaroundTime}
                  </div>
                </div>
              ) : (
                <div className="px-2 py-1.5 rounded-md bg-muted/30 border border-border/40">
                  <span className="text-xs font-medium text-muted-foreground">View Details</span>
                </div>
              )}
            </div>
          ) : shouldHidePrice ? (
            // Price hidden - show blurred placeholder
            <BlurredPrivateData
              placeholderContent={
                <div className="px-2.5 py-1 rounded-md shadow-sm bg-muted/30 border border-border/40">
                  <span className="text-[8px] text-muted-foreground block uppercase tracking-wide font-medium">Price</span>
                  <div className="text-sm font-bold text-foreground">$XXX,XXX</div>
                </div>
              }
              fieldName="Price"
              size="sm"
              onRequestInfo={isOwner ? undefined : () => requestField('price')}
            />
          ) : (
            // Smart price display for helicopter/engine/parts using centralized logic
            <div className={cn(
              "px-2.5 py-1 rounded-md shadow-sm",
              listing.isBuyRequest || listing.listingType === 'buy_request' || listing.listingType === 'rent_request'
                ? "bg-gradient-to-br from-[hsl(45,60%,97%)] to-[hsl(45,50%,93%)] dark:from-[hsl(45,40%,15%)] dark:to-[hsl(45,30%,10%)] border border-[hsl(45,70%,80%)]/60 dark:border-[hsl(45,50%,30%)]/40"
                : "bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/30 border border-slate-200/60 dark:border-slate-700/40"
            )}>
              {priceResult.label && (
                <span className={cn(
                  "text-[8px] block uppercase tracking-wide font-medium",
                  listing.isBuyRequest || listing.listingType === 'buy_request' || listing.listingType === 'rent_request'
                    ? "text-[hsl(45,55%,42%)] dark:text-[hsl(45,60%,60%)]"
                    : "text-muted-foreground"
                )}>{priceResult.label}</span>
              )}
              {priceResult.displayType === 'contact_pricing' || priceResult.displayType === 'request_quote' ? (
                <span className="text-xs text-muted-foreground italic">
                  {priceResult.formattedPrice}
                </span>
              ) : (
                <div className={cn(
                  "text-sm font-bold",
                  listing.isBuyRequest || listing.listingType === 'buy_request' || listing.listingType === 'rent_request'
                    ? "text-[hsl(45,60%,32%)] dark:text-[hsl(45,70%,65%)]"
                    : "text-slate-800 dark:text-slate-200"
                )}>
                  {priceResult.formattedPrice}
                </div>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
              size="icon"
              variant="ghost"
              className={cn(
                "h-9 w-9 rounded-full transition-all duration-200",
                "hover:bg-red-50 dark:hover:bg-red-950/20",
                isFav && "text-red-500 bg-red-50 dark:bg-red-950/20"
              )}
              aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
              onClick={handleFavoriteClick}
            >
              <Heart 
                className={cn(
                  "w-4 h-4 transition-all duration-200",
                  isFav ? "fill-current scale-110" : "scale-100"
                )} 
              />
            </Button>
            <ShareListingButton
              listingId={listing.id}
              title={listing.title}
              price={listing.price}
              location={listing.location}
              listingType={listing.listingType || 'sale'}
              assetType={listing.assetType || 'helicopter'}
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full hover:bg-muted/50"
            />
          </div>
        </div>
      </div>
    </Card>
  );
};